import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MessageCircle, Send, Plus, Music, Brain, Sparkles, ExternalLink, Check, Loader2, GraduationCap, History, Trash2, X, Mail, FileText, ClipboardCopy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config';
import { runAiJob } from '../utils/aiJobs';
import { useAuth } from '../AuthContext';
import EmailHub from './EmailHub';
import DossierGenerator from './DossierGenerator';
import TextParser from './TextParser';
import ManualTracksList from './ManualTracksList';

const API = API_URL;

const DJAssistant = ({ eventContext = null, onPlaylistUpdated = null }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [sessionId, setSessionId] = useState(null); // Mantiene contexto multi-turno con DJ Master
  const [teachInput, setTeachInput] = useState('');
  // Iter92 — Sub-sección "Canciones con rol" (manual openers/closers/etc.)
  const [trackRole, setTrackRole] = useState('opener');
  const [trackTitle, setTrackTitle] = useState('');
  const [trackArtist, setTrackArtist] = useState('');
  const [trackSpotifyUrl, setTrackSpotifyUrl] = useState('');
  const [trackEventHints, setTrackEventHints] = useState('boda,fiesta');
  const [savingTrack, setSavingTrack] = useState(false);
  const [manualTracks, setManualTracks] = useState([]);
  const [loadingManualTracks, setLoadingManualTracks] = useState(false);
  // Iter92 — Bloque B: gestión de playlists Spotify referencia + resync
  const [referencePlaylists, setReferencePlaylists] = useState([]);
  const [loadingRefPlaylists, setLoadingRefPlaylists] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [newRefUrl, setNewRefUrl] = useState('');
  const [newRefLabel, setNewRefLabel] = useState('');
  const [newRefHints, setNewRefHints] = useState('boda');
  const [addingRef, setAddingRef] = useState(false);
  const [knowledge, setKnowledge] = useState([]);
  const [loadingKnowledge, setLoadingKnowledge] = useState(false);
  const [savingBriefing, setSavingBriefing] = useState(false);
  const [addingSongs, setAddingSongs] = useState(false);
  const [briefingText, setBriefingText] = useState(null); // Modal briefing
  const [copyingNoteIdx, setCopyingNoteIdx] = useState(null);
  const scrollRef = useRef(null);

  const eventPlaylistId = eventContext?.playlist_id || null;

  const copyMessageAsNote = async (msg, idx) => {
    if (!eventPlaylistId) return;
    setCopyingNoteIdx(idx);
    try {
      const lines = ['🤖 **Asistente DJ — copiado del chat**', ''];
      if (msg.content) {
        lines.push(msg.content.trim());
        lines.push('');
      }
      if (Array.isArray(msg.songs) && msg.songs.length > 0) {
        lines.push('🎵 **Canciones sugeridas:**');
        msg.songs.forEach((s) => {
          const title = (s.title || '').trim();
          const artist = (s.artist || '').trim();
          if (title) lines.push(`• ${title}${artist ? ' - ' + artist : ''}`);
        });
      }
      const content = lines.join('\n').trim();
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/playlists/${eventPlaylistId}/notes`,
        {
          content,
          visible_to_client: false,
          tipo: 'idea',
          priority: 'media',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('📋 Copiado como nota del evento');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error guardando como nota');
    } finally {
      setCopyingNoteIdx(null);
    }
  };

  // Recopilar TODAS las canciones únicas mencionadas en respuestas del asistente
  const collectedSongs = (() => {
    const seen = new Set();
    const out = [];
    for (const m of conversation) {
      if (m.type !== 'assistant' || !Array.isArray(m.songs)) continue;
      for (const s of m.songs) {
        const t = (s.title || '').trim();
        const a = (s.artist || '').trim();
        if (!t || !a) continue;
        const key = `${t.toLowerCase()}|${a.toLowerCase()}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(s);
      }
    }
    return out;
  })();

  const generateEventBriefing = async () => {
    if (!sessionId) {
      toast.error('Mantén una conversación primero para generar el briefing');
      return;
    }
    setSavingBriefing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await runAiJob({
        startUrl: `${API}/dj-assistant/event-briefing/start`,
        statusBase: `${API}/dj-assistant/jobs`,
        body: { session_id: sessionId, playlist_id: eventPlaylistId },
        config: { headers: { Authorization: `Bearer ${token}` } },
      });
      setBriefingText(response.data.briefing || '');
      if (response.data.saved_to_event) {
        toast.success('Briefing añadido a las notas del evento');
      } else if (eventPlaylistId) {
        toast.success('Briefing generado (revísalo)');
      } else {
        toast.info('Briefing generado. Abre el chat desde un evento para guardarlo en sus notas.');
      }
    } catch (error) {
      console.error('Error generating briefing:', error);
      toast.error(error.response?.data?.detail || 'No se pudo generar el briefing');
    } finally {
      setSavingBriefing(false);
    }
  };

  const addSongsToEvent = async () => {
    if (!eventPlaylistId) {
      toast.error('Abre el chat desde un evento concreto para añadir canciones');
      return;
    }
    if (collectedSongs.length === 0) {
      toast.error('Aún no hay canciones decididas en la conversación');
      return;
    }
    setAddingSongs(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        playlist_id: eventPlaylistId,
        songs: collectedSongs.map(s => ({
          title: s.title,
          artist: s.artist,
          spotify_url: s.spotify_url || null,
          spotify_uri: s.spotify_uri || null,
          youtube_url: s.youtube_url || null,
          year: s.year || null,
          genre: s.genre || null,
          note: s.reason || s.note || null,
        }))
      };
      const response = await axios.post(
        `${API}/dj-assistant/add-songs-to-event`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { added_to_playlist, skipped, created_in_library, reused_from_library } = response.data;
      if (added_to_playlist > 0) {
        const parts = [`${added_to_playlist} ${added_to_playlist === 1 ? 'canción añadida' : 'canciones añadidas'} a la playlist del evento`];
        const detail = [];
        if (created_in_library > 0) detail.push(`${created_in_library} nueva${created_in_library === 1 ? '' : 's'} en biblioteca`);
        if (reused_from_library > 0) detail.push(`${reused_from_library} ya en biblioteca`);
        if (skipped > 0) detail.push(`${skipped} ya en la playlist`);
        if (detail.length > 0) parts.push(`(${detail.join(' · ')})`);
        toast.success(parts.join(' '));
      } else if (skipped > 0) {
        toast.info(`Las ${skipped} canciones ya estaban en la playlist del evento`);
      } else {
        toast.info('Sin canciones nuevas para añadir');
      }
    } catch (error) {
      console.error('Error adding songs to event:', error);
      toast.error(error.response?.data?.detail || 'No se pudieron añadir las canciones');
    } finally {
      setAddingSongs(false);
    }
  };

  // Auto-scroll al final cuando hay nuevos mensajes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation]);

  // Cargar conocimiento cuando se abre la pestaña
  useEffect(() => {
    if (activeTab === 'knowledge' && knowledge.length === 0) {
      loadKnowledge();
    }
    // Iter92 — Cargar manual tracks al abrir pestaña "teach"
    if (activeTab === 'teach' && manualTracks.length === 0) {
      loadManualTracks();
    }
    // Iter92 Bloque B — Cargar reference playlists al abrir pestaña "teach"
    if (activeTab === 'teach' && referencePlaylists.length === 0) {
      loadReferencePlaylists();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: knowledge
  }, [activeTab]);

  const loadKnowledge = async () => {
    setLoadingKnowledge(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dj-assistant/knowledge`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKnowledge(response.data.knowledge || []);
    } catch (error) {
      console.error('Error loading knowledge:', error);
    } finally {
      setLoadingKnowledge(false);
    }
  };

  const sendMessage = async () => {
    if (!message.trim() || loading) return;

    const userMessage = message.trim();
    setMessage('');
    
    // Añadir mensaje del usuario a la conversación
    setConversation(prev => [...prev, { 
      type: 'user', 
      content: userMessage,
      timestamp: new Date()
    }]);

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await runAiJob({
        startUrl: `${API}/dj-assistant/chat/start`,
        statusBase: `${API}/dj-assistant/jobs`,
        body: {
          message: userMessage,
          event_context: eventContext,
          session_id: sessionId  // null en el primer mensaje, backend devolverá uno nuevo
        },
        config: { headers: { Authorization: `Bearer ${token}` } },
      });

      // Guardar session_id devuelto por el backend para mantener contexto en próximos turnos
      if (response.data.session_id && !sessionId) {
        setSessionId(response.data.session_id);
      }

      // Añadir respuesta del asistente
      setConversation(prev => [...prev, {
        type: 'assistant',
        content: response.data.response,
        songs: response.data.songs,
        memories_used: response.data.used_memories,
        used_web_search: response.data.used_web_search,
        timestamp: new Date()
      }]);

    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error al comunicarse con el asistente');
      setConversation(prev => [...prev, {
        type: 'error',
        content: 'Lo siento, hubo un error. Por favor, intenta de nuevo.',
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const teachAssistant = async () => {
    if (!teachInput.trim()) {
      toast.error('Escribe algo para enseñar al asistente');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/dj-assistant/teach`,
        { lesson: teachInput.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      setTeachInput('');
      
      // Recargar conocimiento
      loadKnowledge();

      // Mostrar tags detectados
      if (response.data.tags_detected?.length > 0) {
        toast.info(`Tags detectados: ${response.data.tags_detected.join(', ')}`);
      }

    } catch (error) {
      console.error('Error teaching assistant:', error);
      toast.error('Error al enseñar al asistente');
    } finally {
      setLoading(false);
    }
  };

  const deleteKnowledge = async (lessonId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/dj-assistant/knowledge/${lessonId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Conocimiento eliminado');
      setKnowledge(prev => prev.filter(k => k.id !== lessonId));
    } catch (error) {
      console.error('Error deleting knowledge:', error);
      toast.error('Error al eliminar');
    }
  };

  // ============================================
  // Iter92 — Canciones con rol manual (openers/closers/bachata_early/peak/imprescindible/warmup)
  // ============================================
  const loadManualTracks = async () => {
    setLoadingManualTracks(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dj-assistant/manual-tracks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setManualTracks(response.data.tracks || []);
    } catch (error) {
      console.error('Error loading manual tracks:', error);
    } finally {
      setLoadingManualTracks(false);
    }
  };

  const teachTrack = async () => {
    if (!trackTitle.trim()) {
      toast.error('Pon al menos el título de la canción');
      return;
    }
    setSavingTrack(true);
    try {
      const token = localStorage.getItem('token');
      const hints = trackEventHints
        .split(',')
        .map(h => h.trim())
        .filter(h => h.length > 0);
      const payload = {
        role: trackRole,
        tracks: [{
          title: trackTitle.trim(),
          artist: trackArtist.trim() || undefined,
          spotify_url: trackSpotifyUrl.trim() || undefined,
        }],
        event_type_hints: hints.length > 0 ? hints : undefined,
        label: `Manual ${trackRole} — Luis Herrera`,
      };
      const response = await axios.post(
        `${API}/dj-assistant/teach-tracks`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Canción añadida como "${trackRole}" garantizado ✅`);
      // Limpiar inputs (mantener rol y hints)
      setTrackTitle('');
      setTrackArtist('');
      setTrackSpotifyUrl('');
      // Recargar lista
      loadManualTracks();
    } catch (error) {
      console.error('Error teaching track:', error);
      toast.error(error?.response?.data?.detail || 'Error al añadir canción');
    } finally {
      setSavingTrack(false);
    }
  };

  const deleteManualTrack = async (trackId) => {
    if (!window.confirm('¿Borrar esta canción-rol? El Wizard dejará de incluirla garantizada.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/dj-assistant/manual-tracks/${trackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Canción-rol eliminada');
      setManualTracks(prev => prev.filter(t => t.id !== trackId));
    } catch (error) {
      console.error('Error deleting manual track:', error);
      toast.error('Error al eliminar');
    }
  };

  // ============================================
  // Iter92 Bloque B — Playlists Spotify referencia + Re-sync
  // ============================================
  const loadReferencePlaylists = async () => {
    setLoadingRefPlaylists(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dj-assistant/reference-playlists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferencePlaylists(response.data.playlists || []);
    } catch (error) {
      console.error('Error loading reference playlists:', error);
    } finally {
      setLoadingRefPlaylists(false);
    }
  };

  const addReferencePlaylist = async () => {
    if (!newRefUrl.trim() || !newRefLabel.trim()) {
      toast.error('Pon URL y etiqueta');
      return;
    }
    setAddingRef(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/dj-assistant/reference-playlists`,
        {
          url: newRefUrl.trim(),
          label: newRefLabel.trim(),
          event_type_hints: newRefHints.split(',').map(h => h.trim()).filter(h => h),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Playlist de referencia añadida. Pulsa Resincronizar para cargar sus canciones.');
      setNewRefUrl('');
      setNewRefLabel('');
      loadReferencePlaylists();
    } catch (error) {
      console.error('Error adding ref playlist:', error);
      toast.error(error?.response?.data?.detail || 'Error al añadir');
    } finally {
      setAddingRef(false);
    }
  };

  const deleteReferencePlaylist = async (id, label) => {
    if (!window.confirm(`¿Borrar "${label}" y todas sus canciones? Esta acción no se puede deshacer.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/dj-assistant/reference-playlists/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Playlist eliminada');
      setReferencePlaylists(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting ref playlist:', error);
      toast.error('Error al eliminar');
    }
  };

  const resyncReferencePlaylists = async () => {
    setResyncing(true);
    try {
      const token = localStorage.getItem('token');
      toast.info('🔄 Resincronizando playlists Spotify... (puede tardar 20-40s)');
      const response = await axios.post(
        `${API}/dj-assistant/reference-playlists/resync`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      const s = response.data.summary || {};
      toast.success(
        `✅ Sincronizadas ${response.data.playlists_processed} playlists · ` +
        `+${s.added || 0} añadidas · -${s.removed || 0} quitadas · ${s.unchanged || 0} sin cambios`
      );
      loadReferencePlaylists();
    } catch (error) {
      console.error('Error resyncing:', error);
      toast.error(error?.response?.data?.detail || 'Error al resincronizar');
    } finally {
      setResyncing(false);
    }
  };

  const addSongToLibrary = async (song) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/dj-assistant/add-song`,
        {
          title: song.title,
          artist: song.artist,
          spotify_url: song.spotify_url,
          spotify_uri: song.spotify_uri,
          genre: song.genre,
          year: song.year,
          energy: song.energy,
          bpm: song.bpm
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.already_exists) {
        toast.info(response.data.message);
      } else {
        toast.success(response.data.message);
      }

      // Marcar la canción como añadida en la UI
      setConversation(prev => prev.map(msg => {
        if (msg.songs) {
          return {
            ...msg,
            songs: msg.songs.map(s => 
              s.title === song.title && s.artist === song.artist
                ? { ...s, added: true, already_existed: response.data.already_exists }
                : s
            )
          };
        }
        return msg;
      }));

    } catch (error) {
      console.error('Error adding song:', error);
      toast.error('Error al añadir la canción');
    }
  };

  // Descarga MP3 + añade a biblioteca (reusa endpoint /api/trending/download-to-library)
  const downloadMp3ForSong = async (song) => {
    const toastId = toast.loading(`⏬ Descargando "${song.title}"…`, { duration: Infinity });
    // Marcar como "downloading" en UI
    setConversation(prev => prev.map(msg => {
      if (msg.songs) {
        return {
          ...msg,
          songs: msg.songs.map(s =>
            s.title === song.title && s.artist === song.artist
              ? { ...s, downloading: true }
              : s
          ),
        };
      }
      return msg;
    }));
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/trending/download-to-library`,
        { title: song.title, artist: song.artist || '' },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 }
      );
      toast.success(`✅ MP3 descargado (${res.data.file_size_mb} MB)`, { id: toastId });
      setConversation(prev => prev.map(msg => {
        if (msg.songs) {
          return {
            ...msg,
            songs: msg.songs.map(s =>
              s.title === song.title && s.artist === song.artist
                ? { ...s, downloading: false, mp3_downloaded: true, added: true }
                : s
            ),
          };
        }
        return msg;
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error descargando MP3', { id: toastId });
      setConversation(prev => prev.map(msg => {
        if (msg.songs) {
          return {
            ...msg,
            songs: msg.songs.map(s =>
              s.title === song.title && s.artist === song.artist
                ? { ...s, downloading: false }
                : s
            ),
          };
        }
        return msg;
      }));
    }
  };

  const youtubeSearchUrl = (s) =>
    `https://www.youtube.com/results?search_query=${encodeURIComponent(`${s.artist || ''} ${s.title}`.trim())}`;

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Ejemplos de preguntas
  const exampleQuestions = [
    "5 canciones para fiesta años 90 en español",
    "Canción para entrada de novios al banquete",
    "Música para hora loca estilo reggaeton",
    "Canciones para público de 50 años",
    "Éxitos de boda que nunca fallan"
  ];

  return (
    <>
      {/* Botón flotante */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button
            className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg shadow-purple-500/30 transition-all hover:scale-110"
            size="icon"
            data-testid="dj-assistant-fab"
          >
            <Sparkles className="h-6 w-6 text-white" />
          </Button>
        </SheetTrigger>

        <SheetContent 
          side="right" 
          className="w-full sm:w-[450px] bg-slate-900 border-slate-700 p-0 h-screen dj-sheet-safe"
          style={{ display: 'grid', gridTemplateRows: 'auto 1fr' }}
        >
          <SheetHeader
            className="p-4 border-b border-slate-700 bg-gradient-to-r from-purple-900/50 to-pink-900/50"
            style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }}
          >
            <SheetTitle className="text-white flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                <Music className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-lg font-bold">Master DJ - Asistente IA</div>
                <div className="text-xs text-slate-400 font-normal">Tu experto musical personal</div>
              </div>
            </SheetTitle>
            {/* Indicador de evento activo */}
            {eventContext && eventContext.nombre_evento && (
              <div className="mt-2 p-2 bg-emerald-900/30 border border-emerald-700/50 rounded-lg">
                <p className="text-xs text-emerald-400 flex items-center gap-1">
                  <Check className="h-3 w-3" />
                  Contexto: <span className="font-medium text-emerald-300">{eventContext.nombre_evento}</span>
                </p>
                {eventContext.canciones_prohibidas && (
                  <p className="text-xs text-red-400 mt-1">🚫 Hay canciones prohibidas</p>
                )}
                {eventContext.canciones_imprescindibles && (
                  <p className="text-xs text-yellow-400">⭐ Hay canciones imprescindibles</p>
                )}
              </div>
            )}
          </SheetHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="overflow-hidden" style={{ display: 'grid', gridTemplateRows: 'auto 1fr' }}>
            <TabsList className={`grid ${isAdmin ? 'grid-cols-6' : 'grid-cols-3'} bg-slate-800 m-2 rounded-lg`}>
              <TabsTrigger value="chat" className="data-[state=active]:bg-purple-600 text-xs sm:text-sm">
                <MessageCircle className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Chat</span>
              </TabsTrigger>
              <TabsTrigger value="teach" className="data-[state=active]:bg-purple-600 text-xs sm:text-sm">
                <GraduationCap className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Enseñar</span>
              </TabsTrigger>
              <TabsTrigger value="knowledge" className="data-[state=active]:bg-purple-600 text-xs sm:text-sm">
                <Brain className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Memoria</span>
              </TabsTrigger>
              {/* Pegar Texto - Solo visible para admin */}
              {isAdmin && (
                <TabsTrigger value="paste" className="data-[state=active]:bg-teal-600 text-xs sm:text-sm" data-testid="tab-paste-text">
                  <Plus className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Texto</span>
                </TabsTrigger>
              )}
              {/* Email Hub - Solo visible para admin */}
              {isAdmin && (
                <TabsTrigger value="delicatto" className="data-[state=active]:bg-amber-600 text-xs sm:text-sm">
                  <Mail className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Email</span>
                </TabsTrigger>
              )}
              {/* Dossier - Solo visible para admin */}
              {isAdmin && (
                <TabsTrigger value="dossier" className="data-[state=active]:bg-orange-600 text-xs sm:text-sm">
                  <FileText className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Dossier</span>
                </TabsTrigger>
              )}
            </TabsList>

            {/* Tab: Chat */}
            <TabsContent value="chat" className="overflow-hidden data-[state=inactive]:hidden" style={{ display: 'grid', gridTemplateRows: conversation.length > 0 ? 'auto 1fr auto' : '1fr auto' }}>
              {/* Barra superior: indicador de sesión + botón nueva conversación */}
              {conversation.length > 0 && (
                <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-slate-700/50 bg-slate-900/50 flex-wrap">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Brain className="h-3 w-3 text-purple-400" />
                    <span>Recordando contexto · {conversation.filter(m => m.type === 'user').length} {conversation.filter(m => m.type === 'user').length === 1 ? 'mensaje' : 'mensajes'}</span>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <Button
                      onClick={generateEventBriefing}
                      disabled={savingBriefing || !sessionId}
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-purple-300 hover:text-white hover:bg-purple-900/40"
                      data-testid="dj-master-event-briefing"
                      title={eventPlaylistId ? 'Genera un briefing de 6 líneas y lo añade a las notas del evento' : 'Genera un briefing del evento (no se guardará: abre el chat desde un evento para guardarlo)'}
                    >
                      {savingBriefing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileText className="h-3 w-3 mr-1" />}
                      Resumen del evento
                    </Button>
                    {eventPlaylistId && collectedSongs.length > 0 && (
                      <Button
                        onClick={addSongsToEvent}
                        disabled={addingSongs}
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-cyan-300 hover:text-white hover:bg-cyan-900/40"
                        data-testid="dj-master-add-songs-to-event"
                        title={`Añade las ${collectedSongs.length} canciones de esta conversación a la playlist del evento (sin duplicar las ya existentes)`}
                      >
                        {addingSongs ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Music className="h-3 w-3 mr-1" />}
                        Añadir {collectedSongs.length} a playlist
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        setConversation([]);
                        setSessionId(null);
                        toast.success('Nueva conversación iniciada');
                      }}
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-slate-300 hover:text-white hover:bg-slate-700"
                      data-testid="dj-master-new-conversation"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Nueva
                    </Button>
                  </div>
                </div>
              )}
              <div 
                className="overflow-y-auto p-4" 
                ref={scrollRef}
              >
                {conversation.length === 0 ? (
                  <div className="space-y-4">
                    <div className="text-center text-slate-400 py-8">
                      <Sparkles className="h-12 w-12 mx-auto mb-4 text-purple-400" />
                      <p className="text-lg font-medium text-white mb-2">¡Hola! Soy Master DJ</p>
                      <p className="text-sm">
                        Pregúntame sobre canciones, géneros o pídeme recomendaciones para tu evento.
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Prueba preguntar:</p>
                      {exampleQuestions.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => setMessage(q)}
                          className="w-full text-left text-sm p-3 rounded-lg bg-slate-800/50 hover:bg-slate-800 text-slate-300 transition-colors border border-slate-700/50 cursor-pointer"
                          type="button"
                        >
                          "{q}"
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {conversation.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] rounded-2xl p-3 ${
                          msg.type === 'user' 
                            ? 'bg-purple-600 text-white rounded-br-sm' 
                            : msg.type === 'error'
                            ? 'bg-red-900/50 text-red-200 rounded-bl-sm'
                            : 'bg-slate-800 text-slate-200 rounded-bl-sm'
                        }`}>
                          {/* Contenido del mensaje */}
                          <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                          
                          {/* Canciones recomendadas */}
                          {msg.songs && msg.songs.length > 0 && (
                            <div className="mt-3 space-y-2 border-t border-slate-700 pt-3">
                              <p className="text-xs text-slate-400 font-medium">Canciones encontradas:</p>
                              {msg.songs.map((song, sIdx) => (
                                <div 
                                  key={sIdx} 
                                  className="flex items-center justify-between gap-2 p-2 rounded-lg bg-slate-900/50"
                                >
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-white truncate">
                                      {song.title}
                                    </p>
                                    <p className="text-xs text-slate-400 truncate">{song.artist}</p>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {song.spotify_url && (
                                      <a
                                        href={song.spotify_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-1.5 rounded-full bg-green-600 hover:bg-green-500 transition-colors"
                                        title="Abrir en Spotify"
                                      >
                                        <ExternalLink className="h-3 w-3 text-white" />
                                      </a>
                                    )}
                                    <a
                                      href={youtubeSearchUrl(song)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-1.5 rounded-full bg-red-600 hover:bg-red-500 transition-colors"
                                      title="Buscar en YouTube"
                                      data-testid={`dj-yt-${sIdx}`}
                                    >
                                      <ExternalLink className="h-3 w-3 text-white" />
                                    </a>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => downloadMp3ForSong(song)}
                                      disabled={!!song.downloading || song.mp3_downloaded}
                                      className={`h-7 px-2 ${
                                        song.mp3_downloaded
                                          ? 'bg-cyan-900/50 text-cyan-300'
                                          : 'bg-cyan-700/50 hover:bg-cyan-700 text-cyan-200'
                                      }`}
                                      title={song.mp3_downloaded ? 'MP3 descargado' : 'Descargar MP3 a biblioteca'}
                                      data-testid={`dj-mp3-${sIdx}`}
                                    >
                                      {song.downloading ? (
                                        <span className="text-[10px] animate-pulse">…</span>
                                      ) : song.mp3_downloaded ? (
                                        <Check className="h-3 w-3" />
                                      ) : (
                                        <span className="text-[10px] font-bold">MP3</span>
                                      )}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => addSongToLibrary(song)}
                                      disabled={song.added}
                                      className={`h-7 px-2 ${
                                        song.added 
                                          ? 'bg-green-900/50 text-green-400' 
                                          : 'bg-purple-900/50 hover:bg-purple-800 text-purple-300'
                                      }`}
                                      title={song.added ? 'Ya en biblioteca' : 'Añadir a Pendientes Denon'}
                                    >
                                      {song.added ? (
                                        <Check className="h-3 w-3" />
                                      ) : (
                                        <Plus className="h-3 w-3" />
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Memorias usadas */}
                          {msg.memories_used && msg.memories_used.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-slate-700">
                              <p className="text-xs text-purple-400 flex items-center gap-1">
                                <Brain className="h-3 w-3" />
                                Basado en tu experiencia
                              </p>
                            </div>
                          )}

                          {/* Indicador de Web Search */}
                          {msg.used_web_search && (
                            <div className="mt-2 pt-2 border-t border-slate-700">
                              <p className="text-xs text-cyan-400 flex items-center gap-1">
                                <Sparkles className="h-3 w-3" />
                                Información actualizada de internet
                              </p>
                            </div>
                          )}

                          {/* Copiar como nota — solo en respuestas del asistente y con evento */}
                          {msg.type === 'assistant' && eventPlaylistId && (
                            <div className="mt-2 pt-2 border-t border-slate-700">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => copyMessageAsNote(msg, idx)}
                                disabled={copyingNoteIdx === idx}
                                className="h-7 px-2 text-xs text-cyan-300 hover:bg-cyan-500/20"
                                data-testid={`dj-assistant-copy-note-${idx}`}
                                title="Copiar esta respuesta como nota del evento (visible en LiveMode)"
                              >
                                {copyingNoteIdx === idx ? (
                                  <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Guardando…</>
                                ) : (
                                  <><ClipboardCopy className="h-3 w-3 mr-1" /> 📋 Copiar como nota</>
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {loading && (
                      <div className="flex justify-start">
                        <div className="bg-slate-800 rounded-2xl rounded-bl-sm p-3">
                          <Loader2 className="h-5 w-5 animate-spin text-purple-400" />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Input de chat - siempre visible */}
              <div className="p-4 border-t border-slate-700 bg-slate-900">
                <div className="flex gap-2">
                  <Input
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Pregunta sobre música..."
                    className="flex-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    disabled={loading}
                    data-testid="dj-assistant-input"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={loading || !message.trim()}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="dj-assistant-send"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Tab: Enseñar */}
            <TabsContent value="teach" className="flex-1 flex flex-col m-0 p-4 overflow-y-auto data-[state=inactive]:hidden">
              <div className="space-y-4">
                <div className="text-center py-4">
                  <GraduationCap className="h-12 w-12 mx-auto mb-3 text-yellow-400" />
                  <h3 className="text-lg font-medium text-white mb-2">Enseña al Asistente</h3>
                  <p className="text-sm text-slate-400">
                    Comparte tus experiencias para que el asistente aprenda y te dé mejores recomendaciones.
                  </p>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Ejemplos:</p>
                  <ul className="text-sm text-slate-300 space-y-2">
                    <li className="flex gap-2">
                      <span className="text-purple-400">•</span>
                      "El Himno a Logroño funcionó muy bien en boda con edad media 50 años en Logroño"
                    </li>
                    <li className="flex gap-2">
                      <span className="text-purple-400">•</span>
                      "Para novios rumanos, Dragostea din tei siempre arrasa en la hora loca"
                    </li>
                    <li className="flex gap-2">
                      <span className="text-purple-400">•</span>
                      "En comuniones funciona muy bien Aserejé para el momento de baile con niños"
                    </li>
                  </ul>
                </div>

                <textarea
                  value={teachInput}
                  onChange={(e) => setTeachInput(e.target.value)}
                  placeholder="Escribe tu experiencia o consejo..."
                  className="w-full h-32 p-3 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="dj-assistant-teach-input"
                />

                <Button
                  onClick={teachAssistant}
                  disabled={loading || !teachInput.trim()}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-medium"
                  data-testid="dj-assistant-teach-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <GraduationCap className="h-4 w-4 mr-2" />
                  )}
                  Enseñar al Asistente
                </Button>

                {/* ============================================ */}
                {/* Iter92 — Sub-sección: Canciones con ROL      */}
                {/* (openers/closers/bachata_early/peak garantizados) */}
                {/* ============================================ */}
                <div className="mt-6 pt-6 border-t border-slate-700">
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-medium text-white mb-1">🎵 Canciones con rol garantizado</h3>
                    <p className="text-xs text-slate-400">
                      Estas canciones siempre entrarán en la posición elegida — saltan el filtro de género.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Rol en la playlist</label>
                      <select
                        value={trackRole}
                        onChange={(e) => setTrackRole(e.target.value)}
                        className="w-full p-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        data-testid="dj-assistant-track-role-select"
                      >
                        <option value="opener">🟢 Opener (1ª-3ª canción, sin quemar hits)</option>
                        <option value="warmup">🔵 Warmup (calentamiento pos 4-10)</option>
                        <option value="bachata_early">💃 Bachata early (entre las 10 primeras)</option>
                        <option value="peak">🔥 Peak (zona pico baile)</option>
                        <option value="imprescindible">⭐ Imprescindible (en algún punto)</option>
                        <option value="closer">🔴 Closer (cierre garantizado)</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={trackTitle}
                        onChange={(e) => setTrackTitle(e.target.value)}
                        placeholder="Título canción*"
                        className="p-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        data-testid="dj-assistant-track-title-input"
                      />
                      <input
                        type="text"
                        value={trackArtist}
                        onChange={(e) => setTrackArtist(e.target.value)}
                        placeholder="Artista"
                        className="p-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        data-testid="dj-assistant-track-artist-input"
                      />
                    </div>

                    <input
                      type="text"
                      value={trackSpotifyUrl}
                      onChange={(e) => setTrackSpotifyUrl(e.target.value)}
                      placeholder="URL Spotify (opcional — recomendado si hay versiones similares)"
                      className="w-full p-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      data-testid="dj-assistant-track-spotify-input"
                    />

                    <input
                      type="text"
                      value={trackEventHints}
                      onChange={(e) => setTrackEventHints(e.target.value)}
                      placeholder="Tipos de evento (separados por coma): boda,fiesta,comunion..."
                      className="w-full p-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      data-testid="dj-assistant-track-hints-input"
                    />

                    <Button
                      onClick={teachTrack}
                      disabled={savingTrack || !trackTitle.trim()}
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium"
                      data-testid="dj-assistant-teach-track-btn"
                    >
                      {savingTrack ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <>🎵 </>
                      )}
                      Añadir como "{trackRole}" garantizado
                    </Button>
                  </div>

                  {/* Lista de canciones-rol existentes */}
                  <div className="mt-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-slate-400 uppercase tracking-wide">
                        Mis canciones con rol ({manualTracks.length})
                      </p>
                      <button
                        onClick={loadManualTracks}
                        className="text-xs text-purple-400 hover:text-purple-300"
                        data-testid="dj-assistant-reload-manual-tracks"
                      >
                        ↻ Recargar
                      </button>
                    </div>
                    {loadingManualTracks ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-purple-400" />
                      </div>
                    ) : (
                      <ManualTracksList
                        tracks={manualTracks}
                        setTracks={setManualTracks}
                        api={API}
                        token={localStorage.getItem('token')}
                        onDelete={deleteManualTrack}
                      />
                    )}
                  </div>
                </div>

                {/* ============================================ */}
                {/* Iter92 Bloque B — Playlists Spotify referencia + Re-sync */}
                {/* ============================================ */}
                <div className="mt-6 pt-6 border-t border-slate-700">
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-medium text-white mb-1">🎧 Playlists Spotify de referencia</h3>
                    <p className="text-xs text-slate-400">
                      Tus playlists Spotify alimentan el orden + patrones del Wizard. Edítalas en Spotify y pulsa Resincronizar.
                    </p>
                  </div>

                  {/* Botón principal Resincronizar */}
                  <Button
                    onClick={resyncReferencePlaylists}
                    disabled={resyncing || loadingRefPlaylists}
                    className="w-full bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white font-medium mb-4"
                    data-testid="dj-assistant-resync-spotify-btn"
                  >
                    {resyncing ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Resincronizando...</>
                    ) : (
                      <>🔄 Resincronizar todas las playlists</>
                    )}
                  </Button>

                  {/* Lista de playlists configuradas */}
                  <div className="mb-4">
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">
                      Playlists configuradas ({referencePlaylists.length})
                    </p>
                    {loadingRefPlaylists ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-green-400" />
                      </div>
                    ) : referencePlaylists.length === 0 ? (
                      <p className="text-xs text-slate-500 text-center py-3">
                        Aún no hay playlists configuradas.
                      </p>
                    ) : (
                      <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                        {referencePlaylists.map((p) => (
                          <div
                            key={p.id}
                            className="flex items-center gap-2 bg-slate-800/60 rounded px-2 py-1.5 text-xs"
                            data-testid={`dj-assistant-ref-playlist-${p.id}`}
                          >
                            <span className="text-base flex-shrink-0">🎵</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-white truncate">{p.label}</div>
                              <div className="text-slate-400 truncate">
                                {p.event_type_hints?.length > 0 && (
                                  <span className="text-slate-500">{p.event_type_hints.join(', ')}</span>
                                )}
                                {p.last_synced_at ? (
                                  <span className="text-green-400 ml-2">
                                    · ↻ {new Date(p.last_synced_at).toLocaleDateString('es-ES')} ({p.last_sync_tracks_count} tracks)
                                  </span>
                                ) : (
                                  <span className="text-amber-400 ml-2">· sin sincronizar</span>
                                )}
                              </div>
                            </div>
                            <a
                              href={p.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-400 hover:text-green-300 px-1 flex-shrink-0"
                              title="Abrir en Spotify"
                            >
                              ↗
                            </a>
                            <button
                              onClick={() => deleteReferencePlaylist(p.id, p.label)}
                              className="text-red-400 hover:text-red-300 px-1 flex-shrink-0"
                              title="Borrar"
                              data-testid={`dj-assistant-delete-ref-playlist-${p.id}`}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Formulario añadir nueva playlist */}
                  <div className="space-y-2 bg-slate-800/40 rounded-lg p-3">
                    <p className="text-xs text-slate-400">Añadir nueva playlist referencia</p>
                    <input
                      type="text"
                      value={newRefUrl}
                      onChange={(e) => setNewRefUrl(e.target.value)}
                      placeholder="URL Spotify (https://open.spotify.com/playlist/...)"
                      className="w-full p-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                      data-testid="dj-assistant-new-ref-url"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={newRefLabel}
                        onChange={(e) => setNewRefLabel(e.target.value)}
                        placeholder="Etiqueta (ej: Aniversario)"
                        className="p-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                        data-testid="dj-assistant-new-ref-label"
                      />
                      <input
                        type="text"
                        value={newRefHints}
                        onChange={(e) => setNewRefHints(e.target.value)}
                        placeholder="Tipos evento (csv)"
                        className="p-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                        data-testid="dj-assistant-new-ref-hints"
                      />
                    </div>
                    <Button
                      onClick={addReferencePlaylist}
                      disabled={addingRef || !newRefUrl.trim() || !newRefLabel.trim()}
                      variant="outline"
                      className="w-full border-green-600 text-green-400 hover:bg-green-600 hover:text-white text-sm"
                      data-testid="dj-assistant-add-ref-playlist-btn"
                    >
                      {addingRef ? (
                        <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Añadiendo...</>
                      ) : (
                        <>➕ Añadir playlist</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Tab: Memoria/Conocimiento */}
            <TabsContent value="knowledge" className="flex-1 flex flex-col m-0 data-[state=inactive]:hidden">
              <ScrollArea className="flex-1 p-4">
                {loadingKnowledge ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
                  </div>
                ) : knowledge.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Brain className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Aún no has enseñado nada al asistente.</p>
                    <p className="text-sm mt-2">Ve a la pestaña "Enseñar" para empezar.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-400">
                      {knowledge.length} experiencia{knowledge.length !== 1 ? 's' : ''} guardada{knowledge.length !== 1 ? 's' : ''}
                    </p>
                    {knowledge.map((item) => (
                      <Card key={item.id} className="bg-slate-800/50 border-slate-700 p-3">
                        <div className="flex justify-between gap-2">
                          <div className="flex-1">
                            <p className="text-sm text-white">{item.lesson}</p>
                            {item.tags && item.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {item.tags.map((tag, i) => (
                                  <Badge 
                                    key={`${tag}-${i}`} 
                                    variant="outline" 
                                    className="text-xs bg-purple-900/30 text-purple-300 border-purple-700"
                                  >
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteKnowledge(item.id)}
                            className="h-8 w-8 p-0 text-slate-400 hover:text-red-400 hover:bg-red-900/20"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>

              <div className="p-4 border-t border-slate-700">
                <Button
                  onClick={loadKnowledge}
                  variant="outline"
                  className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  <History className="h-4 w-4 mr-2" />
                  Actualizar
                </Button>
              </div>
            </TabsContent>

            {/* Tab: Pegar Texto - Solo para admin */}
            {isAdmin && (
              <TabsContent value="paste" className="flex-1 flex flex-col m-0 data-[state=inactive]:hidden overflow-hidden">
                <TextParser
                  eventContext={eventContext}
                  onEventCreated={() => {
                    // ClientTextImportDialog ya muestra su propio toast detallado
                    // ("Aplicado: 2 peticiones con momento, ...") — evitar el doble toast.
                    if (onPlaylistUpdated) onPlaylistUpdated();
                  }}
                />
              </TabsContent>
            )}

            {/* Tab: Email Hub - Solo para admin */}
            {isAdmin && (
              <TabsContent value="delicatto" className="flex-1 flex flex-col m-0 data-[state=inactive]:hidden overflow-hidden">
                <EmailHub onEventCreated={() => {
                  toast.success('Evento creado - Recarga el calendario para verlo');
                }} />
              </TabsContent>
            )}

            {/* Tab: Dossier/Portfolio Generator - Solo para admin */}
            {isAdmin && (
              <TabsContent value="dossier" className="flex-1 flex flex-col m-0 data-[state=inactive]:hidden overflow-hidden p-2">
                <DossierGenerator />
              </TabsContent>
            )}
          </Tabs>
        </SheetContent>
      </Sheet>

      {/* Modal con briefing generado */}
      {briefingText !== null && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setBriefingText(null)}
          data-testid="dj-master-briefing-modal"
        >
          <div
            className="bg-slate-900 border border-purple-500/40 rounded-xl shadow-2xl max-w-lg w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-purple-400" />
                <h3 className="text-base font-semibold text-white">Briefing del evento</h3>
              </div>
              <Button
                onClick={() => setBriefingText(null)}
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-slate-400 hover:text-white"
                data-testid="dj-master-briefing-close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <pre className="whitespace-pre-wrap text-sm text-slate-200 bg-slate-800/60 rounded-lg p-3 border border-slate-700/50 max-h-[60vh] overflow-y-auto font-sans">
              {briefingText || '(vacío)'}
            </pre>
            <div className="mt-3 flex items-center justify-between gap-2">
              <span className="text-xs text-slate-400">
                {eventPlaylistId ? 'Guardado en las notas del evento' : 'Abre el chat desde un evento para guardarlo'}
              </span>
              <Button
                onClick={() => {
                  navigator.clipboard?.writeText(briefingText || '');
                  toast.success('Copiado al portapapeles');
                }}
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                data-testid="dj-master-briefing-copy"
              >
                Copiar
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DJAssistant;
